import {useMemo} from 'react'
import type {INavMenuItem} from '../components/layout/MenuItems'
import {useAppSelector} from '../store/store-hooks'
import {isEnabled} from '~/src/featureFlags'
import {passesPlatformAdminGate} from '~/src/routes/routeConfig'
import {hasModulePermission, resolveLegacyModuleKey} from '~/src/routes/hasModulePermission'

/**
 * Module key aliases for role-specific permission mappings
 * Some menu items use a generic moduleKey (like 'users_roles') but company/agency admins
 * have role-specific keys (like 'company_users_roles' or 'agency_users_roles')
 *
 * Key patterns:
 * - users_roles: Top-level nav, maps to company_users_roles/agency_users_roles submodules
 * - positions: Nav item, maps to company_positions submodule
 * - manage_orders: MSP/Agency nav, checks for manage_orders OR company_manage_orders
 * - orders: Generic orders check across different contexts
 */
const MODULE_KEY_ALIASES: Record<string, string[]> = {
  users_roles: ['company_users_roles', 'agency_users_roles', 'agency-users'],
  positions: ['company_positions'],
  // Orders nav item can be controlled by either manage_orders (MSP/Agency) or company_manage_orders (Company)
  manage_orders: ['company_manage_orders', 'agency-job-orders', 'agency-assignments'],
  // Service orders submodule alias
  service_orders: ['company_orders', 'agency_orders'],
  // Agency category permissions from BE use "agency-*" prefixed keys
  // Map standard menu moduleKeys to their agency-prefixed equivalents
  dashboard: ['agency-company-management'], // Dashboard is available to all agency users
  manage_companies: ['agency-company-management'],
  candidates: ['agency-candidates'],
  manage_profile: ['agency-billing', 'agency-reports'],
  pre_fill: ['agency-job-orders'],
}

/**
 * Helper to find a module key in submodules
 */
function findInSubmodules(
  modulePermissions: Array<{
    key: string
    canView?: boolean
    submodules?: Array<{key: string; canView?: boolean}>
  }>,
  targetKey: string,
): boolean | null {
  for (const mod of modulePermissions) {
    if (mod.submodules && Array.isArray(mod.submodules)) {
      const found = mod.submodules.find((s) => s.key === targetKey)
      if (found) {
        return found.canView ?? false
      }
    }
  }
  return null
}

/** An item tied to a feature flag shows only while that flag is on, whatever the permissions say. */
const passesFeatureFlag = (item: INavMenuItem): boolean =>
  !item.featureFlag || isEnabled(item.featureFlag)

/**
 * Hook to filter menu items based on user permissions
 *
 * Filters menu items based on the canView permission for each item's moduleKey.
 * Items without a moduleKey are always visible.
 * Platform admins (isPlatform = true) see all items.
 * Items marked platformAdminOnly are shown only to platform admins (never while permissions are
 * unknown), whatever their moduleKey grants.
 * Supports module key aliases for role-specific permission mappings.
 */
export const useFilteredMenuItems = (items: INavMenuItem[]): INavMenuItem[] => {
  const {backendRolePermissions} = useAppSelector((state) => state.root)

  return useMemo(() => {
    const isPlatform = backendRolePermissions?.isPlatform
    const gateItems = (menuItems: INavMenuItem[]): INavMenuItem[] =>
      menuItems
        .filter((item) => passesPlatformAdminGate(item, isPlatform) && passesFeatureFlag(item))
        .map((item) =>
          item.subMenuItems ? {...item, subMenuItems: gateItems(item.subMenuItems)} : item,
        )

    // If no permissions loaded, show all items (fallback behavior) except platform-admin-only ones
    if (!backendRolePermissions) {
      return gateItems(items)
    }

    const modulePermissions = backendRolePermissions.modulePermissions

    // If no modulePermissions array, fall back to showing all items
    if (!modulePermissions || !Array.isArray(modulePermissions)) {
      return gateItems(items)
    }

    // Helper function to check if a module has canView permission
    // Searches both top-level modules and submodules
    const canViewModule = (requestedKey?: string): boolean => {
      // Items without moduleKey are always visible
      if (!requestedKey) return true
      // A session signed in before a module existed reads it from its legacy key (Finance RBAC).
      const moduleKey = resolveLegacyModuleKey(
        modulePermissions as Parameters<typeof resolveLegacyModuleKey>[0],
        requestedKey,
      )

      // TODO(feature-flag): dashboardMetrics — remove when flag is deleted
      if (
        moduleKey === 'dashboard' &&
        backendRolePermissions.isPlatform &&
        !isEnabled('dashboardMetrics')
      ) {
        return false
      }

      // Check top-level modules first
      const topLevelPermission = modulePermissions.find((m) => m.key === moduleKey)
      if (topLevelPermission) {
        if (topLevelPermission.canView) {
          return true
        }
        // Top-level canView is false — still show if any submodule has canView: true
        if (topLevelPermission.submodules?.some((s) => s.canView)) {
          return true
        }
        return false
      }

      // Check submodules of each top-level module
      for (const module of modulePermissions) {
        if (module.submodules && Array.isArray(module.submodules)) {
          const subPermission = module.submodules.find((s) => s.key === moduleKey)
          if (subPermission) {
            return subPermission.canView ?? false
          }
        }
      }

      // Check for module key aliases (role-specific permission mappings)
      // This handles cases like 'users_roles' -> 'company_users_roles' for company admins
      const aliases = MODULE_KEY_ALIASES[moduleKey]
      if (aliases && aliases.length > 0) {
        for (const aliasKey of aliases) {
          // Check if alias exists as a direct module
          const aliasModule = modulePermissions.find((m) => m.key === aliasKey)
          if (aliasModule) {
            return aliasModule.canView ?? false
          }

          // Check if alias exists as a submodule (uses helper to reduce nesting)
          const submoduleResult = findInSubmodules(modulePermissions, aliasKey)
          if (submoduleResult !== null) {
            return submoduleResult
          }
        }
      }

      // Module not found - default to hidden for security
      return false
    }

    /**
     * An item keyed on a SUBMODULE (e.g. My Signatures: settings > esign_signing) asks the route
     * guard's own question, with the same helper and the same reading of its answer
     * (RouteRenderer in AppRoutes): an explicit deny hides it, an absent node hides it from all
     * but Platform. canViewModule cannot answer it: it reads one level of submodules and would
     * fall back to the parent module's flags, offering a link the guard then refuses.
     */
    const canViewItem = (item: INavMenuItem): boolean => {
      if (item.moduleKey && item.submoduleKey) {
        const held = hasModulePermission(modulePermissions, item.moduleKey, item.submoduleKey)
        return held === true || (held === undefined && !!backendRolePermissions.isPlatform)
      }
      return canViewModule(item.moduleKey)
    }

    // Filter items and their submenus
    const filterItems = (menuItems: INavMenuItem[]): INavMenuItem[] => {
      return menuItems
        .filter(
          (item) =>
            passesPlatformAdminGate(item, isPlatform) &&
            passesFeatureFlag(item) &&
            canViewItem(item),
        )
        .map((item) => {
          // If item has subMenuItems, filter those too
          if (item.subMenuItems && item.subMenuItems.length > 0) {
            const filteredSubItems = filterItems(item.subMenuItems)
            // If all sub-items are filtered out, hide the parent too
            if (filteredSubItems.length === 0) {
              return null
            }
            return {...item, subMenuItems: filteredSubItems}
          }
          return item
        })
        .filter((item): item is INavMenuItem => item !== null)
    }

    return filterItems(items)
  }, [items, backendRolePermissions])
}

export default useFilteredMenuItems
